import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { resolveNotificationHref } from "@/features/notifications/resolveNotificationHref"
import { useNotifications } from "@/features/notifications/NotificationsProvider"
import { NotificationItem } from "@/widgets/notifications/NotificationItem"
import { LoadingBlock } from "@/widgets/LoadingBlock"

type Props = {
  onClose: () => void
}

export const NotificationPopover = ({ onClose }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { recent, loading, unreadCount, markAllRead, openNotification, refreshRecent } = useNotifications()
  const firstFocusRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    void refreshRecent()
  }, [refreshRecent])

  useEffect(() => {
    firstFocusRef.current?.focus()
  }, [])

  const handleMarkAll = async () => {
    try {
      await markAllRead()
    } catch {
      /** Ошибка уже откатит состояние; тост поднимется через error boundary на вызывающей стороне. */
    }
  }

  const handleShowAll = () => {
    onClose()
    navigate("/profile?section=notifications")
  }

  const handleActivate = (id: string) => {
    const item = recent.find((n) => n.id === id)
    if (!item) return
    onClose()
    openNotification(item)
    const href = resolveNotificationHref(item)
    if (href) navigate(href)
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">{t("notifications.popover.title")}</p>
          <p className="text-xs text-gray-500 truncate">
            {unreadCount > 0
              ? t("notifications.popover.unreadSummary", { count: unreadCount })
              : t("notifications.popover.allRead")}
          </p>
        </div>
        {unreadCount > 0 ? (
          <button
            ref={firstFocusRef}
            type="button"
            onClick={() => void handleMarkAll()}
            className="shrink-0 text-xs font-medium text-brand-500 hover:text-brand-600 px-2 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 transition-colors"
          >
            {t("notifications.popover.markAllRead")}
          </button>
        ) : null}
      </div>

      <div className="overflow-y-auto max-h-[min(70vh,28rem)] divide-y divide-gray-100" role="list">
        {loading && recent.length === 0 ? (
          <LoadingBlock variant="section" message={t("common.loading")} className="!py-8 px-6 text-sm text-gray-500" />
        ) : recent.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-notification-off-line text-xl text-gray-400" aria-hidden />
            </div>
            {t("notifications.popover.empty")}
          </div>
        ) : (
          recent.map((n) => (
            <div role="listitem" key={n.id}>
              <NotificationItem notification={n} onActivate={() => handleActivate(n.id)} />
            </div>
          ))
        )}
      </div>

      <div className="border-t border-gray-100 p-2">
        <button
          type="button"
          onClick={handleShowAll}
          className="w-full text-center text-sm font-semibold text-brand-500 hover:bg-[#F5E6D3]/50 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 transition-colors"
        >
          {t("notifications.popover.showAll")}
        </button>
      </div>
    </>
  )
}
